import { normalizeError } from 'core/common/errors';
import { SpanAttributes, SpanName } from 'core/common/observability/entities';
import { ATTRS_ANALYTICS_EVENT_NAME } from 'core/common/observability/entities/SemanticAttributes';
import { ObservabilitySystem } from 'core/common/observability/interfaces';
import { ErrorMonitoring } from 'core/common/services/errorMonitoring';
import { TikTokEvent, TiktokParams } from '../../entities';
import { TikTokService } from '../../interfaces';
import { TikTokServiceDecorator } from './TikTokServiceDecorator';

export class TikTokServiceWithMonitoring extends TikTokServiceDecorator {
  constructor(
    tikTokService: TikTokService,
    private readonly observabilitySystem: ObservabilitySystem,
    private readonly errorMonitoringService: ErrorMonitoring,
    private readonly receiveAdditionalObservabilityAttributes?: () => SpanAttributes,
  ) {
    super(tikTokService);
  }

  track(eventName: TikTokEvent, params?: TiktokParams) {
    if (!super.isTtqRequired()) {
      try {
        return super.track(eventName, params);
      } catch {
        this.errorMonitoringService.captureError('TikTok track failed', {
          tags: {
            error_message: 'TikTok track failed',
            code: 'tiktok_track_failed_when_not_required',
          },
        });
        return;
      }
    }

    if (super.isTtqRequired() && !super.isTtqAvailable()) {
      try {
        return super.track(eventName, params);
      } catch {
        this.errorMonitoringService.captureError('TikTok track failed', {
          tags: {
            error_message: 'TikTok track failed',
            code: 'tiktok_track_failed_when_required_not_available',
          },
        });
        return;
      }
    }

    const span = this.observabilitySystem.startSpan(SpanName.TRACK_ANALYTICS_TIK_TOK);
    const spanAttributes = {
      [ATTRS_ANALYTICS_EVENT_NAME]: eventName,
      ...(this.receiveAdditionalObservabilityAttributes
        ? this.receiveAdditionalObservabilityAttributes()
        : {}),
    };
    span.setAttributes(spanAttributes);

    try {
      const result = super.track(eventName, params);
      span.succeed();
      return result;
    } catch (err) {
      const error = normalizeError(err);
      span.fail(error);
      return;
    }
  }
}
